import { useState, useEffect, useCallback } from "react";
import { avatarBg } from "@/lib/avatar";
import { useNavigate } from "react-router-dom";
import { useAuth, api } from "@/App";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { 
  Sparkles, 
  ArrowLeft, 
  Loader2, 
  Crown, 
  Lock, 
  CheckCircle, 
  XCircle,
  Clock,
  DollarSign,
  Star
} from "lucide-react";
import { Car as CarIcon, MapPin as MapPinIcon, ExternalLink as ExternalLinkIcon, CalendarHeart } from "lucide-react";
import PlanDateDialog from "@/components/PlanDateDialog";
import SafetyShareDialog from "@/components/SafetyShareDialog";
import Navigation from "@/components/Navigation";
import DateCountdown from "@/components/DateCountdown";
import DateIdeasFeed from "@/components/DateIdeasFeed";
import VoucherCard from "@/components/VoucherCard";
import { useAppMode } from "@/context/AppModeContext";
import { useLocale } from "@/i18n/LocaleContext";
import RespondOfferModal from "@/components/RespondOfferModal";
import CounterOfferResponseModal from "@/components/CounterOfferResponseModal";
import AgeVerificationModal from "@/components/AgeVerificationModal";
import PayPalEmailWarning from "@/components/PayPalEmailWarning";
import DateReviewModal from "@/components/DateReviewModal";
import CompletedDateModal from "@/components/CompletedDateModal";
import SendOfferModal from "@/components/SendOfferModal";
import { fireHeartsConfetti } from "@/lib/confetti";
import PaymentModal from "@/components/PaymentModal";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

export default function PremiumOffers() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isPayer } = useAppMode();
  const { t } = useLocale();
  const [loading, setLoading] = useState(true);
  const [slotsData, setSlotsData] = useState(null);
  const [sentOffers, setSentOffers] = useState([]);
  const [receivedOffers, setReceivedOffers] = useState([]);
  const [purchaseDialog, setPurchaseDialog] = useState(false);
  const [balance, setBalance] = useState(0);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [respondDialog, setRespondDialog] = useState(false);
  const [counterRespondDialog, setCounterRespondDialog] = useState(false);
  const [selectedCounterOffer, setSelectedCounterOffer] = useState(null);
  const [paymentDialog, setPaymentDialog] = useState(false);
  const [selectedPaymentOffer, setSelectedPaymentOffer] = useState(null);
  const [userCredits, setUserCredits] = useState(0);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [selectedConfirmOffer, setSelectedConfirmOffer] = useState(null);
  const [ageModalOpen, setAgeModalOpen] = useState(false);
  const [ageGatedAction, setAgeGatedAction] = useState(null);
  const [confirmationCode, setConfirmationCode] = useState("");
  const [submittingConfirmation, setSubmittingConfirmation] = useState(false);
  const [reviewedIds, setReviewedIds] = useState([]);
  const [reviewRatings, setReviewRatings] = useState({});
  const [reviewDialog, setReviewDialog] = useState(false);
  const [selectedReviewOffer, setSelectedReviewOffer] = useState(null);
  const [detailOffer, setDetailOffer] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [rebookWoman, setRebookWoman] = useState(null);
  const [rebookOpen, setRebookOpen] = useState(false);
  const [planPickerOpen, setPlanPickerOpen] = useState(false);

  const handleRebook = () => {
    const other = isPayer ? detailOffer?.woman : detailOffer?.man;
    setDetailOpen(false);
    if (isPayer) {
      setRebookWoman(other);
      setRebookOpen(true);
    } else {
      navigate(`/profile/${other?.user_id}`);
    }
  };

  const fetchMyReviews = useCallback(async () => {
    try {
      const response = await api.get("/date-reviews/mine");
      setReviewedIds(response.data.reviewed_offer_ids || []);
      setReviewRatings(response.data.ratings || {});
    } catch (error) {
      // silent - non-critical
    }
  }, []);

  useEffect(() => {
    fetchMyReviews();
  }, [fetchMyReviews]);

  const openReview = (offer) => {
    setSelectedReviewOffer(offer);
    setReviewDialog(true);
  };

  const renderReviewAction = (offer) => {
    if (offer.status !== "completed") return null;
    const other = isPayer ? offer.woman : offer.man;
    const detailsBtn = (
      <Button
        size="sm"
        variant="outline"
        className="border-white/15 text-white/80 hover:bg-white/10"
        onClick={() => {
          setDetailOffer(offer);
          setDetailOpen(true);
        }}
        data-testid="date-details-btn"
      >
        {t("offers.dateDetails")}
      </Button>
    );
    if (reviewedIds.includes(offer.offer_id)) {
      return (
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-1.5 text-sm text-amber-400" data-testid="reviewed-indicator">
            <Star className="w-4 h-4 fill-amber-400" />
            You rated this date {reviewRatings[offer.offer_id]}/5
          </div>
          {detailsBtn}
        </div>
      );
    }
    if (offer.test_run || other?.ratings_enabled === false) return detailsBtn;
    return (
      <div className="flex items-center gap-2 flex-wrap">
        <Button
          size="sm"
          variant="outline"
          className="border-amber-500/40 text-amber-400 hover:bg-amber-500/10"
          onClick={() => openReview(offer)}
          data-testid="rate-date-btn"
        >
          <Star className="mr-2 h-4 w-4" />
          {t("offers.rateDate")}
        </Button>
        {detailsBtn}
      </div>
    );
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      if (isPayer) {
        const [slotsRes, offersRes, balanceRes] = await Promise.all([
          api.get("/premium-slots/status"),
          api.get("/date-offers/sent"),
          api.get("/credits/balance")
        ]);
        setBalance(balanceRes.data.credits);
        setUserCredits(balanceRes.data.credits);
        setSlotsData(slotsRes.data);
        setSentOffers(offersRes.data.offers);
      } else {
        const [offersRes, balanceRes] = await Promise.all([
          api.get("/date-offers/received"),
          api.get("/credits/balance")
        ]);
        setBalance(balanceRes.data.balance || 0);
        setReceivedOffers(offersRes.data.offers);
      }
    } catch (error) {
      console.error("Failed to load data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [user, isPayer]);

  useEffect(() => {
    fetchData();
    
    // Check for payment success from URL params
    const urlParams = new URLSearchParams(window.location.search);
    const paymentStatus = urlParams.get('payment');
    
    if (paymentStatus === 'success') {
      toast.success("Slot purchase successful! Your slot will be activated shortly.");
      // Refresh data after a short delay to allow webhook to process
      setTimeout(() => {
        fetchData();
      }, 2000);
      // Clean URL
      window.history.replaceState({}, '', '/premium-offers');
    } else if (paymentStatus === 'cancelled') {
      toast.error("Payment cancelled");
      window.history.replaceState({}, '', '/premium-offers');
    }
  }, [fetchData]);

  const handlePurchaseSlot = () => {
    setPurchaseDialog(true);
  };

  const handleCancelOffer = async (offerId) => {
    try {
      await api.post(`/date-offers/${offerId}/cancel`);
      toast.success("Offer cancelled, slot is now available");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel offer");
    }
  };

  const handleSimulatePayment = async (offerId) => {
    try {
      const response = await api.post(`/date-offers/${offerId}/simulate-payment`);
      toast.success(response.data.message);
      fireHeartsConfetti();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to simulate payment");
    }
  };

  const handleConfirmDate = async () => {
    if (!confirmationCode || confirmationCode.length !== 6) {
      toast.error("Please enter a 6-digit confirmation code");
      return;
    }

    try {
      setSubmittingConfirmation(true);
      const response = await api.post(
        `/date-offers/${selectedConfirmOffer.offer_id}/confirm`,
        { confirmation_code: confirmationCode }
      );
      
      toast.success(response.data.message);
      fireHeartsConfetti();
      setConfirmDialog(false);
      setConfirmationCode("");
      setSelectedConfirmOffer(null);
      fetchData();
    } catch (error) {
      console.error("Failed to confirm date:", error);
      toast.error(error.response?.data?.detail || "Invalid confirmation code");
    } finally {
      setSubmittingConfirmation(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { variant: "secondary", icon: Clock, text: t("status.pending") },
      payment_pending: { variant: "default", icon: DollarSign, text: t("status.paymentNeeded") },
      paid_pending_confirmation: { variant: "default", icon: CheckCircle, text: t("status.awaitingConfirmation") },
      countered: { variant: "default", icon: Clock, text: t("status.countered") },
      confirmed: { variant: "default", icon: CheckCircle, text: t("status.confirmed") },
      completed: { variant: "default", icon: CheckCircle, text: t("status.completed") },
      declined: { variant: "destructive", icon: XCircle, text: t("status.declined") },
      expired: { variant: "destructive", icon: Clock, text: t("status.expired") },
      cancelled: { variant: "secondary", icon: XCircle, text: t("status.cancelled") },
    };
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    
    return (
      <Badge variant={config.variant} className="flex items-center gap-1">
        <Icon className="h-3 w-3" />
        {config.text}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
      </div>
    );
  }

  // Women's view: Received offers
  if (!isPayer) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <main className="pt-20 pb-24 px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <Button
              variant="ghost"
              onClick={() => navigate(-1)}
              className="mb-6"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              {t("common.back")}
            </Button>

            <div className="mb-8">
              <div className="flex items-center justify-between mb-2">
                <h1 className="text-3xl font-bold">{t("offers.dateOffers")}</h1>
                <div className="text-right">
                  <p className="text-sm text-muted-foreground">{t("common.yourBalance")}</p>
                  <p className="text-2xl font-bold text-primary">${balance.toFixed(2)}</p>
                </div>
              </div>
              <p className="text-muted-foreground">
                {t("offers.reviewRespond")}
              </p>
            </div>

            <VoucherCard />
            <DateIdeasFeed />

            {receivedOffers.length === 0 ? (
              <Card className="p-12 text-center">
                <Sparkles className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <h3 className="text-lg font-semibold mb-2">{t("offers.none")}</h3>
                <p className="text-muted-foreground">
                  {t("offers.noneDesc")}
                </p>
              </Card>
            ) : (
              <div className="space-y-4">
                {receivedOffers.map((offer) => (
                  <Card key={offer.offer_id} className="p-6">
                    <div className="flex gap-4">
                      <img
                        src={offer.man?.photos?.[0] || offer.man?.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(offer.man?.name || "?")}&background=${avatarBg()}&color=fff&size=200`}
                        alt={offer.man?.name}
                        className="h-20 w-20 rounded-lg object-cover cursor-pointer hover:opacity-80 transition-opacity"
                        onClick={() => navigate(`/profile/${offer.man?.user_id}`)}
                        data-testid="offer-man-photo"
                      />
                      <div className="flex-1">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <h3
                              className="font-semibold text-lg cursor-pointer hover:text-primary transition-colors"
                              onClick={() => navigate(`/profile/${offer.man?.user_id}`)}
                              data-testid="offer-man-name"
                            >
                              {offer.man?.name}
                            </h3>
                            <p className="text-sm text-muted-foreground">
                              {offer.man?.age} years old
                            </p>
                          </div>
                          {getStatusBadge(offer.status)}
                        </div>
                        
                        <div className="bg-primary/10 rounded-lg p-4 mb-3">
                          <p className="text-2xl font-bold text-primary mb-2">
                            ${offer.offer_amount} Offer
                          </p>
                          <div className="text-sm space-y-1">
                            <p><strong>Date:</strong> {offer.date_details?.proposed_date}</p>
                            <p><strong>Time:</strong> {offer.date_details?.proposed_time}</p>
                            {offer.date_details?.duration_hours ? (
                              <p data-testid={`offer-duration-${offer.offer_id}`}><strong>Duration:</strong> {offer.date_details.duration_hours} hour{offer.date_details.duration_hours === 1 ? "" : "s"}</p>
                            ) : null}
                            {offer.date_details?.date_type && (
                              <p><strong>Type:</strong> <span className="capitalize">{offer.date_details.date_type.replace('_', ' ')}</span></p>
                            )}
                            <p><strong>Location:</strong> {offer.date_details?.location}</p>
                            {offer.date_details?.transport_allowance > 0 && (
                              <p data-testid="offer-transport-line"><strong>Transport:</strong> +${offer.date_details.transport_allowance} Uber/taxi allowance (100% to the recipient)</p>
                            )}
                            {offer.date_details?.experience_url && (
                              <a href={offer.date_details.experience_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-rose-400 hover:underline" data-testid="offer-experience-link">
                                <ExternalLinkIcon className="w-3.5 h-3.5" /> View the planned Airbnb Experience
                              </a>
                            )}
                            {offer.date_details?.location && ["accepted", "payment_pending", "paid_pending_confirmation"].includes(offer.status) && (
                              <div className="flex items-center gap-4 pt-1">
                                <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(offer.date_details.location)}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline" data-testid="offer-maps-link">
                                  <MapPinIcon className="w-3.5 h-3.5" /> View map
                                </a>
                                <a href={`https://m.uber.com/ul/?action=setPickup&pickup=my_location&dropoff[formatted_address]=${encodeURIComponent(offer.date_details.location)}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline" data-testid="offer-uber-link">
                                  <CarIcon className="w-3.5 h-3.5" /> Get an Uber
                                </a>
                                <SafetyShareDialog offer={offer} />
                              </div>
                            )}
                            {offer.date_details?.message && (
                              <p className="mt-2 text-muted-foreground">
                                &quot;{offer.date_details.message}&quot;
                              </p>
                            )}
                          </div>
                        </div>

                        {offer.status === "pending" && (
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => {
                                setSelectedOffer(offer);
                                if (!user?.age_verified) {
                                  setAgeGatedAction("respond");
                                  setAgeModalOpen(true);
                                } else {
                                  setRespondDialog(true);
                                }
                              }}
                              data-testid="respond-offer-btn"
                            >
                              {t("offers.respond")}
                            </Button>
                          </div>
                        )}

                        {offer.status === "paid_pending_confirmation" && (
                          <div className="space-y-3">
                            <DateCountdown offer={offer} />
                            <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4">
                              <p className="text-sm font-semibold text-green-600 mb-1">
                                💰 Date Paid - Awaiting Confirmation
                              </p>
                              <p className="text-sm text-muted-foreground">
                                ${(offer.woman_payout ?? offer.offer_amount * 0.8).toFixed(2)} is held in escrow for you
                              </p>
                            </div>
                            <Button
                              size="sm"
                              className="w-full"
                              onClick={() => {
                                setSelectedConfirmOffer(offer);
                                setConfirmDialog(true);
                              }}
                            >
                              <CheckCircle className="mr-2 h-4 w-4" />
                              Confirm Date Happened
                            </Button>
                            <p className="text-xs text-muted-foreground text-center">
                              Enter the 6-digit code from your date to release payment
                            </p>
                          </div>
                        )}

                        {offer.status === "completed" && (
                          <div className="mt-1">{renderReviewAction(offer)}</div>
                        )}
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </main>

        {/* Date Confirmation Modal */}
        <Dialog open={confirmDialog} onOpenChange={setConfirmDialog}>
          <DialogContent className="max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Confirm Date Completion</DialogTitle>
              <DialogDescription>
                Enter the 6-digit confirmation code that {selectedConfirmOffer?.man?.name} received after payment
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="bg-primary/10 rounded-lg p-4 text-center">
                <p className="text-sm font-semibold text-primary mb-2">
                  Payment in Escrow
                </p>
                <p className="text-3xl font-bold text-primary">
                  ${selectedConfirmOffer?.offer_amount ? (selectedConfirmOffer.offer_amount * 0.8).toFixed(2) : '0.00'}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Will be added to your balance upon confirmation
                </p>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Confirmation Code</label>
                <input
                  type="text"
                  maxLength="6"
                  placeholder="Enter 6-digit code"
                  value={confirmationCode}
                  onChange={(e) => setConfirmationCode(e.target.value.replace(/\D/g, ''))}
                  className="w-full px-4 py-3 text-center text-2xl font-mono tracking-widest bg-background border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
                <p className="text-xs text-muted-foreground">
                  Ask your date partner for this code
                </p>
              </div>
            </div>

            <DialogFooter className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={() => {
                  setConfirmDialog(false);
                  setConfirmationCode("");
                  setSelectedConfirmOffer(null);
                }}
                className="flex-1"
                disabled={submittingConfirmation}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleConfirmDate}
                className="flex-1"
                disabled={submittingConfirmation || confirmationCode.length !== 6}
              >
                {submittingConfirmation ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Confirming...
                  </>
                ) : (
                  <>
                    <CheckCircle className="mr-2 h-4 w-4" />
                    Confirm & Release Payment
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Respond to Offer Modal */}
        <RespondOfferModal
          isOpen={respondDialog}
          onClose={() => {
            setRespondDialog(false);
            setSelectedOffer(null);
          }}
          offer={selectedOffer}
          onSuccess={() => {
            fetchData();
          }}
        />

        {/* Age Verification Modal */}
        <AgeVerificationModal
          isOpen={ageModalOpen}
          onClose={() => setAgeModalOpen(false)}
          onVerified={() => {
            if (ageGatedAction === "respond") setRespondDialog(true);
            setAgeGatedAction(null);
          }}
        />

        {/* Date Review Modal */}
        <DateReviewModal
          isOpen={reviewDialog}
          onClose={() => {
            setReviewDialog(false);
            setSelectedReviewOffer(null);
          }}
          offer={selectedReviewOffer}
          otherName={selectedReviewOffer?.man?.name}
          onSuccess={fetchMyReviews}
        />

        {/* Completed Date Details Modal */}
        <CompletedDateModal
          isOpen={detailOpen}
          onClose={() => setDetailOpen(false)}
          offer={detailOffer}
          otherUser={detailOffer?.man}
          onRebook={handleRebook}
        />
      </div>
    );
  }

  // Men's view: Slots and sent offers
  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <main className="pt-20 pb-24 px-4 md:px-6">
        <div className="max-w-4xl mx-auto">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="mb-6"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t("common.back")}
          </Button>

          <div className="mb-8">
            <h1 className="text-3xl font-bold mb-2">{t("offers.title")}</h1>
            <p className="text-muted-foreground">
              {t("offers.subtitlePayer")}
            </p>
          </div>

          <VoucherCard />
          <DateIdeasFeed />

          {/* Slots Overview */}
          <Card className="p-6 mb-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-semibold mb-1">{t("offers.slots")}</h2>
                <p className="text-sm text-muted-foreground">
                  {t("offers.slotDesc")}
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <Button variant="outline" onClick={() => setPlanPickerOpen(true)} data-testid="plan-date-btn">
                  <CalendarHeart className="mr-2 h-4 w-4" />
                  {t("offers.planDate")}
                </Button>
                <Button onClick={handlePurchaseSlot}>
                  <Crown className="mr-2 h-4 w-4" />
                  {t("offers.buySlot")}
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-4 bg-green-500/10 rounded-lg">
                <p className="text-3xl font-bold text-green-500">
                  {slotsData?.available_slots || 0}
                </p>
                <p className="text-sm text-muted-foreground">{t("offers.available")}</p>
              </div>
              <div className="text-center p-4 bg-yellow-500/10 rounded-lg">
                <p className="text-3xl font-bold text-yellow-500">
                  {slotsData?.locked_slots || 0}
                </p>
                <p className="text-sm text-muted-foreground">{t("offers.inUse")}</p>
              </div>
              <div className="text-center p-4 bg-gray-500/10 rounded-lg">
                <p className="text-3xl font-bold text-gray-500">
                  {slotsData?.consumed_slots || 0}
                </p>
                <p className="text-sm text-muted-foreground">{t("offers.consumed")}</p>
              </div>
            </div>
          </Card>

          {/* Sent Offers */}
          <div className="mb-4">
            <h2 className="text-xl font-semibold">{t("offers.sentOffers")}</h2>
          </div>

          {sentOffers.length === 0 ? (
            <Card className="p-12 text-center">
              <Lock className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-lg font-semibold mb-2">{t("offers.noneSent")}</h3>
              <p className="text-muted-foreground mb-4">
                {t("offers.noneSentDesc")}
              </p>
              <div className="flex flex-col sm:flex-row gap-2 justify-center">
                <Button variant="outline" onClick={() => setPlanPickerOpen(true)} data-testid="plan-date-empty-btn">
                  <CalendarHeart className="mr-2 h-4 w-4" />
                  {t("offers.planDate")}
                </Button>
                <Button onClick={handlePurchaseSlot}>
                  <Crown className="mr-2 h-4 w-4" />
                  {t("offers.buyFirstSlot")}
                </Button>
              </div>
            </Card>
          ) : (
            <div className="space-y-4">
              {sentOffers.map((offer) => (
                <Card key={offer.offer_id} className="p-6">
                  <div className="flex gap-4">
                    <img
                      src={offer.woman?.photos?.[0] || offer.woman?.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(offer.woman?.name || "?")}&background=${avatarBg()}&color=fff&size=200`}
                      alt={offer.woman?.name}
                      className="h-20 w-20 rounded-lg object-cover cursor-pointer hover:opacity-80 transition-opacity"
                      onClick={() => navigate(`/profile/${offer.woman?.user_id}`)}
                      data-testid="offer-woman-photo"
                    />
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h3
                            className="font-semibold text-lg cursor-pointer hover:text-primary transition-colors"
                            onClick={() => navigate(`/profile/${offer.woman?.user_id}`)}
                            data-testid="offer-woman-name"
                          >
                            {offer.woman?.name}
                          </h3>
                          <p className="text-sm text-muted-foreground">
                            Offer sent {new Date(offer.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          {offer.test_run && (
                            <Badge className="bg-amber-500/15 text-amber-400 border border-amber-500/40" data-testid="test-run-badge">🧪 {t("common.testRun")}</Badge>
                          )}
                          {getStatusBadge(offer.status)}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-2xl font-bold text-primary">
                          ${offer.offer_amount}
                        </span>
                        <span className="text-sm text-muted-foreground">
                          • {offer.date_details?.proposed_date}
                        </span>
                      </div>

                      {offer.status === "paid_pending_confirmation" && offer.confirmation_code && (
                        <div className="mb-3">
                          <DateCountdown offer={offer} />
                        <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4">
                          <p className="text-sm font-semibold text-green-600 mb-2">
                            {offer.test_run ? "🧪 Test payment simulated — here's your demo code:" : "✅ Payment Complete - Share this code at your date:"}
                          </p>
                          <div className="flex items-center justify-between bg-background rounded-lg p-3 mb-2">
                            <span className="text-3xl font-mono font-bold tracking-widest text-primary">
                              {offer.confirmation_code}
                            </span>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                navigator.clipboard.writeText(offer.confirmation_code);
                                toast.success("Code copied!");
                              }}
                            >
                              Copy
                            </Button>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {offer.test_run
                              ? "Test run: your AI companion will confirm automatically in a few moments — no real money moves."
                              : `Your date needs to enter this code to confirm the date happened and receive their $${(offer.woman_payout ?? offer.offer_amount * 0.8).toFixed(2)} payment`}
                          </p>
                        </div>
                        </div>
                      )}

                      <div className="flex gap-2">
                        {(offer.status === "pending" || offer.status === "countered" || offer.status === "payment_pending") && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleCancelOffer(offer.offer_id)}
                          >
                            {t("offers.cancelOffer")}
                          </Button>
                        )}
                        {offer.status === "payment_pending" && (
                          offer.test_run ? (
                            <Button
                              size="sm"
                              className="bg-amber-500 hover:bg-amber-600 text-black font-semibold"
                              onClick={() => handleSimulatePayment(offer.offer_id)}
                              data-testid="simulate-payment-btn"
                            >
                              🧪 {t("offers.simulatePayment")}
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              onClick={() => {
                                setSelectedPaymentOffer(offer);
                                setPaymentDialog(true);
                              }}
                              data-testid="complete-payment-btn"
                            >
                              {t("credits.completePayment")}
                            </Button>
                          )
                        )}
                        {offer.status === "countered" && (
                          <Button
                            size="sm"
                            onClick={() => {
                              setSelectedCounterOffer(offer);
                              if (!user?.age_verified) {
                                setAgeGatedAction("counter");
                                setAgeModalOpen(true);
                              } else {
                                setCounterRespondDialog(true);
                              }
                            }}
                          >
                            {t("offers.viewCounter")}
                          </Button>
                        )}
                        {renderReviewAction(offer)}
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Purchase Slot Dialog */}
      <Dialog open={purchaseDialog} onOpenChange={setPurchaseDialog}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Purchase Premium Offer Slot</DialogTitle>
            <DialogDescription>
              Each slot costs $50 and is reusable until an offer is accepted
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-primary/10 rounded-lg p-4 text-center">
              <p className="text-4xl font-bold text-primary mb-2">$50</p>
              <p className="text-sm text-muted-foreground">Per Slot</p>
            </div>
            
            <div className="space-y-2 text-sm">
              <p className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5" />
                <span>Send money offers to your matches</span>
              </p>
              <p className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5" />
                <span>Reuse slot if offer is declined</span>
              </p>
              <p className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5" />
                <span>20% platform fee on accepted dates</span>
              </p>
            </div>

            <div className="text-center">
              <PayPalEmailWarning />
              <p className="text-sm text-muted-foreground mb-4 mt-3">
                Payment via PayPal ($52 total: $50 slot + $2 handling fee)
              </p>
              
              {/* PayPal Button Form */}
              <div className="flex justify-center">
                <style>{`.pp-VFXQV6CVBMQ9Y{text-align:center;border:none;border-radius:0.25rem;min-width:11.625rem;padding:0 2rem;height:2.625rem;font-weight:bold;background-color:#FFD140;color:#000000;font-family:"Helvetica Neue",Arial,sans-serif;font-size:1rem;line-height:1.25rem;cursor:pointer;}`}</style>
                <form 
                  action="https://www.paypal.com/ncp/payment/VFXQV6CVBMQ9Y" 
                  method="post" 
                  target="_blank" 
                  style={{display:'inline-grid', justifyItems:'center', alignContent:'start', gap:'0.5rem'}}
                >
                  <input className="pp-VFXQV6CVBMQ9Y" type="submit" value="Buy Now" />
                  <img src="https://www.paypalobjects.com/images/Debit_Credit_APM.svg" alt="cards" />
                  <section style={{fontSize: '0.75rem'}}> 
                    Powered by <img src="https://www.paypalobjects.com/paypal-ui/logos/svg/paypal-wordmark-color.svg" alt="paypal" style={{height:'0.875rem', verticalAlign:'middle'}}/>
                  </section>
                </form>
              </div>
              
              <p className="text-xs text-muted-foreground mt-4">
                After payment, return to this page. Your slot will be activated within 30 seconds.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setPurchaseDialog(false)}
              className="w-full"
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Counter-Offer Response Modal */}
      <CounterOfferResponseModal
        isOpen={counterRespondDialog}
        onClose={() => {
          setCounterRespondDialog(false);
          setSelectedCounterOffer(null);
        }}
        offer={selectedCounterOffer}
        onSuccess={() => {
          fetchData();
        }}
      />

      {/* Age Verification Modal */}
      <AgeVerificationModal
        isOpen={ageModalOpen}
        onClose={() => setAgeModalOpen(false)}
        onVerified={() => {
          if (ageGatedAction === "counter") setCounterRespondDialog(true);
          setAgeGatedAction(null);
        }}
      />

      {/* Payment Modal */}
      <PaymentModal
        isOpen={paymentDialog}
        onClose={() => {
          setPaymentDialog(false);
          setSelectedPaymentOffer(null);
        }}
        offer={selectedPaymentOffer}
        userCredits={userCredits}
        onSuccess={() => {
          fetchData();
        }}
      />

      {/* Date Review Modal */}
      <DateReviewModal
        isOpen={reviewDialog}
        onClose={() => {
          setReviewDialog(false);
          setSelectedReviewOffer(null);
        }}
        offer={selectedReviewOffer}
        otherName={selectedReviewOffer?.woman?.name}
        onSuccess={fetchMyReviews}
      />

      {/* Completed Date Details Modal */}
      <CompletedDateModal
        isOpen={detailOpen}
        onClose={() => setDetailOpen(false)}
        offer={detailOffer}
        otherUser={detailOffer?.woman}
        onRebook={handleRebook}
      />

      {/* Plan a Date: pick anyone from the member list */}
      <PlanDateDialog
        isOpen={planPickerOpen}
        onClose={() => setPlanPickerOpen(false)}
        onSelect={(c) => {
          setPlanPickerOpen(false);
          setRebookWoman(c);
          setRebookOpen(true);
        }}
      />

      {/* Rebook: Send New Offer */}
      <SendOfferModal
        isOpen={rebookOpen}
        onClose={() => {
          setRebookOpen(false);
          setRebookWoman(null);
        }}
        woman={rebookWoman}
        onSuccess={() => {
          setRebookOpen(false);
          setRebookWoman(null);
          fetchData();
        }}
      />
    </div>
  );
}
